import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top that indicates scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handler = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="flex-1 p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-blue-100 rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph component for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-base text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const InfrastructureAndEcosystemPractices: React.FC = () => (
  <div className="relative overflow-hidden space-y-8 px-4 py-10 md:py-6 md:px-8">
    <ScrollProgress />

    {/* Introduction */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20,
      }}
      viewport={{ once: true }}
      className="max-w-6xl mx-auto text-center space-y-4"
    >
      <h2 className="text-3xl font-bold text-primary-dark">
        Infrastructure and Ecosystem Partnerships in UcWaves
      </h2>
      <Paragraph className="text-gray-700 max-w-6xl mx-auto">
        UcWaves leverages the robust, battle-tested infrastructure of the
        Ethereum ecosystem and its surrounding services to ensure high
        availability, security, and performance. By partnering with
        industry-leading providers and open-source protocols, we build on a
        foundation that scales with our community’s growth and trust that
        critical building blocks—node access, data indexing, transaction
        bundling, and account abstraction—are handled by experts. This document
        thanks our partners and highlights how their offerings contribute to a
        seamless UcWaves experience.
      </Paragraph>
    </motion.div>

    {/* Two-column block */}
    <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
      <div className="flex-1 flex flex-col space-y-6">
        {/* Section 1 */}
        <SectionCard title="1. Ethereum and EVM Compatibility">
          <Paragraph>
            At UcWaves, we embrace the Ethereum Virtual Machine (EVM) standard,
            ensuring full compatibility with existing smart contracts, developer
            tooling, and wallet integrations. By aligning with EVM, we:
          </Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Maintain access to a vast pool of audited libraries (OpenZeppelin,
              Uniswap V3 core, etc.)
            </li>
            <li>Leverage mature development frameworks (Foundry)</li>
            <li>
              Ensure seamless wallet support (MetaMask, Coinbase Wallet, Reown)
            </li>
          </ul>
        </SectionCard>

        {/* Section 2 */}
        <SectionCard title="2. RPC and Developer APIs via Alchemy">
          <Paragraph>
            For remote procedure calls (RPC), webhooks, and real-time
            notifications, UcWaves relies on Alchemy. Their turnkey node
            infrastructure and enhanced APIs offer:
          </Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Scalable RPC Access: Multi-region and load-balanced endpoints that
              handle traffic spikes without degradation.
            </li>
            <li>
              Webhooks & Event Streams: Instant notifications on contract events
              and transaction confirmations, powering our real-time UI updates.
            </li>
            <li>
              Enhanced Debugging: Tools like the Alchemy Supernode dashboard
              help our developers quickly trace issues.
            </li>
          </ul>
          <Paragraph>
            We thank the Alchemy team for their responsiveness and continuous
            innovation, which frees our engineers to focus on core features
            rather than node fleet maintenance.
          </Paragraph>
        </SectionCard>

        {/* Section 3 */}
        <SectionCard title="3. Data Indexation with The Graph">
          <Paragraph>
            To provide robust queries and analysis—such as historical voting
            results, staking metrics, and jeNAUN balances—UcWaves integrates The
            Graph protocol:
          </Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Subgraph Definitions: Custom subgraphs index all main contracts
              (Controller, StakingRewards, Vesting, etc.) and expose GraphQL
              APIs for our frontend and dashboards.
            </li>
            <li>
              Decentralized Indexing: By running our own hosted service and
              relying on community indexers, we gain redundancy and
              decentralization.
            </li>
            <li>
              Composability: Other developers can build on our subgraphs,
              fostering ecosystem expansion.
            </li>
          </ul>
          <Paragraph>
            The collaboration of The Graph team on editing tools and performance
            enhancements has been invaluable in delivering fast, low-latency
            queries.
          </Paragraph>
        </SectionCard>
      </div>

      <div className="flex-1 flex flex-col space-y-6">
        {/* Section 4 */}
        <SectionCard title="4. Account Abstraction with Reown AppKit">
          <Paragraph>
            UcWaves offers advanced wallet features—fee abstraction, social
            recovery, and multi-key configurations—by integrating Reown’s
            ERC-4337 AppKit:
          </Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Bundler & Paymaster Services: Users can pay gas in jeNAUNs ERC-20
              or benefit from sponsored transactions, simplifying onboarding.
            </li>
            <li>
              Smart Wallet Contracts: Reown’s audited templates ensure session
              keys and secure recovery flows.
            </li>
            <li>
              Seamless Developer SDK: AppKit’s React hook and TypeScript support
              accelerate our dApp integration.
            </li>
          </ul>
          <Paragraph>
            We thank the Reown team for their collaborative spirit and clear
            documentation, which made ERC-4337 adoption simple.
          </Paragraph>
        </SectionCard>

        {/* Section 5 */}
        <SectionCard title="5. Complementary Open-Source and Community Tools">
          <Paragraph>
            Beyond our key partners, UcWaves depends on many open-source
            contributors and protocols:
          </Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Uniswap V3 Core: Oracle libraries and pool contracts that support
              our TWAP functionality.
            </li>
            <li>
              OpenZeppelin Libraries: Security-hardened modules for SafeERC20,
              AccessControl, and math utilities.
            </li>
            <li>
              Ethers.js & wagmi: Reliable client-side libraries for contract
              interaction and state management.
            </li>
          </ul>
          <Paragraph>
            By relying on the community, UcWaves benefits from collective peer
            review and rapid innovation.
          </Paragraph>
        </SectionCard>

        {/* Section 6 */}
        <SectionCard title="6. Reliability, Performance, and Future Roadmap">
          <Paragraph>Thanks to these partnerships, UcWaves achieves:</Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              High Availability: Multi-provider and regional failovers ensure
              the dApp remains responsive.
            </li>
            <li>
              Consistent UX: Real-time data via webhooks and subgraphs ensure
              users see accurate balances and event confirmations.
            </li>
            <li>
              Scalability: As our user base grows, we can increase throughput
              without rearchitecting core systems.
            </li>
          </ul>
          <Paragraph>Looking forward, we plan to explore:</Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              Decentralized RPC gateways (e.g., community-run nodes) for
              increased resilience.
            </li>
            <li>
              Cross-chain indexing to support future L2 or sidechain
              integrations.
            </li>
          </ul>
        </SectionCard>
      </div>
    </div>

    {/* Acknowledgments */}
    <div className="max-w-6xl mx-auto space-y-6 mt-20">
      <h2 className="text-3xl font-bold text-primary-dark text-center">
        Acknowledgments
      </h2>
      <Paragraph className="text-gray-700 max-w-6xl mx-auto">
        UcWaves extends sincere thanks to:
      </Paragraph>
      <ul className="list-disc list-inside text-base text-gray-800">
        <li>
          <strong>The Ethereum</strong> Foundation and wider EVM community for
          establishing a global standard.
        </li>
        <li>
          <strong>Base</strong>for maintaining a performant, developer-friendly
          L2 network that powers our infrastructure.
        </li>
        <li>
          <strong>Alchemy </strong>for powering our RPC, webhooks, and developer
          APIs.
        </li>
        <li>
          <strong>The Graph</strong> for delivering efficient, decentralized
          data indexing.
        </li>
        <li>
          <strong>Reown </strong>for enabling secure ERC-4337 smart wallets and
          advanced user experiences.
        </li>
        <li>
          <strong>Open-source maintainers </strong>and DeFi protocol teams whose
          libraries and contracts underpin our platform.
        </li>
      </ul>
      <Paragraph className="text-gray-700 max-w-6xl mx-auto">
        Together, these partners empower UcWaves to focus on delivering an
        uncensored, community-driven opinion platform, confident that our
        infrastructure is in expert hands.
      </Paragraph>
    </div>
  </div>
);

export default InfrastructureAndEcosystemPractices;
